<script setup>
import { computed } from 'vue';

import MessageMeta from '../MessageMeta.vue';

import { emitter } from 'shared/helpers/mitt';
import { useMessageContext } from '../provider.js';
import { useI18n } from 'vue-i18n';

import { BUS_EVENTS } from 'shared/constants/busEvents';
import { MESSAGE_VARIANTS, ORIENTATION } from '../constants';

const props = defineProps({
  hideMeta: { type: Boolean, default: false },
});

const {
  variant,
  orientation,
  inReplyTo,
  shouldGroupWithNext,
  contentAttributes,
  content,
} = useMessageContext();
const { t } = useI18n();

const varaintBaseMap = {
  // Outgoing messages (agent) — keep original bubble, make text white
  [MESSAGE_VARIANTS.AGENT]: 'bg-n-solid-blue text-n-white/30',
  [MESSAGE_VARIANTS.PRIVATE]:
    'bg-n-solid-amber text-n-amber-12 [&_.prosemirror-mention-node]:font-semibold',
  [MESSAGE_VARIANTS.USER]: 'bg-n-slate-4 text-n-white/30',
  [MESSAGE_VARIANTS.ACTIVITY]: 'bg-n-alpha-1 text-n-slate-11 text-sm',
  // Assistant/bot messages — keep original bubble, make text white
  [MESSAGE_VARIANTS.BOT]: 'bg-n-solid-iris text-n-white/30',
  [MESSAGE_VARIANTS.TEMPLATE]: 'bg-n-solid-iris text-n-white',
  [MESSAGE_VARIANTS.ERROR]: 'bg-n-ruby-4 text-n-ruby-12',
  [MESSAGE_VARIANTS.EMAIL]: 'w-full',
  [MESSAGE_VARIANTS.UNSUPPORTED]:
    'bg-n-solid-amber/70 border border-dashed border-n-amber-12 text-n-amber-12',
};

const orientationMap = {
  [ORIENTATION.LEFT]:
    'left-bubble rounded-xl ltr:rounded-bl-sm rtl:rounded-br-sm',
  [ORIENTATION.RIGHT]:
    'right-bubble rounded-xl ltr:rounded-br-sm rtl:rounded-bl-sm',
  [ORIENTATION.CENTER]: 'rounded-md',
};

const flexOrientationClass = computed(() => {
  const map = {
    [ORIENTATION.LEFT]: 'justify-start',
    [ORIENTATION.RIGHT]: 'justify-end',
    [ORIENTATION.CENTER]: 'justify-center',
  };

  return map[orientation.value];
});

const isDeleted = computed(() => {
  const hasAttr = contentAttributes.value?.deleted;
  const text = (content?.value ?? '').toString();
  const hasMarker = text.trim().startsWith('⛔');
  return hasAttr || hasMarker;
});

const messageClass = computed(() => {
  const base = isDeleted.value
    ? // Deleted message: force black text on light mode
      'bg-n-slate-4 text-black dark:text-n-slate-12'
    : varaintBaseMap[variant.value];
  const classToApply = [base];

  if (variant.value !== MESSAGE_VARIANTS.ACTIVITY) {
    classToApply.push(orientationMap[orientation.value]);
  } else {
    classToApply.push('rounded-lg');
  }

  return classToApply;
});

const scrollToMessage = () => {
  emitter.emit(BUS_EVENTS.SCROLL_TO_MESSAGE, {
    messageId: inReplyTo.value.id,
  });
};

const shouldShowMeta = computed(
  () =>
    !props.hideMeta &&
    !shouldGroupWithNext.value &&
    variant.value !== MESSAGE_VARIANTS.ACTIVITY
);

// Choose meta text color based on state
const metaTextClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.PRIVATE) {
    return 'text-n-amber-12/50';
  }
  if (isDeleted.value) {
    // Deleted message: black in light mode, readable gray in dark mode
    return 'text-black dark:text-n-slate-11';
  }
  // Default (non-deleted)
  return 'text-n-slate-11';
});

const replyPreviewTextClass = computed(() => {
  if (variant.value === MESSAGE_VARIANTS.USER) {
    return 'text-black dark:text-n-slate-12';
  }

  return 'text-white dark:text-n-slate-12';
});

const replyToPreview = computed(() => {
  if (!inReplyTo) return '';

  const { content: replyContent, attachments } = inReplyTo.value;

  if (replyContent) return replyContent;
  if (attachments?.length) {
    const firstAttachment = attachments[0];
    const fileType = firstAttachment.fileType ?? firstAttachment.file_type;
    const map = {
      image: 'CHAT_LIST.ATTACHMENTS.image.CONTENT',
      audio: 'CHAT_LIST.ATTACHMENTS.audio.CONTENT',
      video: 'CHAT_LIST.ATTACHMENTS.video.CONTENT',
      file: 'CHAT_LIST.ATTACHMENTS.file.CONTENT',
      location: 'CHAT_LIST.ATTACHMENTS.location.CONTENT',
      contact: 'CHAT_LIST.ATTACHMENTS.contact.CONTENT',
      ig_reel: 'CHAT_LIST.ATTACHMENTS.video.CONTENT',
      share: 'CHAT_LIST.ATTACHMENTS.file.CONTENT',
      fallback: 'CHAT_LIST.ATTACHMENTS.file.CONTENT',
      story_mention: 'CHAT_LIST.ATTACHMENTS.video.CONTENT',
    };
    const i18nKey = map[fileType] || 'CHAT_LIST.ATTACHMENTS.file.CONTENT';
    return t(i18nKey);
  }

  return t('CONVERSATION.REPLY_MESSAGE_NOT_FOUND');
});
</script>

<template>
  <div
    class="text-sm"
    :class="[
      messageClass,
      {
        'max-w-lg': variant !== MESSAGE_VARIANTS.EMAIL,
      },
    ]"
  >
    <div
      v-if="inReplyTo"
      class="p-2 -mx-1 mb-2 rounded-lg cursor-pointer bg-n-alpha-black1"
      :class="replyPreviewTextClass"
      @click.stop="scrollToMessage"
    >
      <span class="break-all line-clamp-2">
        {{ replyToPreview }}
      </span>
    </div>
    <slot />
    <MessageMeta
      v-if="shouldShowMeta"
      :class="[
        flexOrientationClass,
        variant === MESSAGE_VARIANTS.EMAIL ? 'px-3 pb-3' : '',
        metaTextClass,
      ]"
      class="mt-2"
    />
  </div>
</template>
